import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Car, Users, Bell, ShieldCheck, Landmark, Settings, LayoutDashboard, Plus, UserPlus, Power, Receipt, type LucideIcon } from "lucide-react";
import { api, errorMessage, logoUrl } from "../lib/api";
import type { Tenant } from "../lib/types";
import { Button } from "../components/core/Button";
import { StatusBadge } from "../components/core/Badges";
import { ConfirmDialog } from "../components/core/ConfirmDialog";
import { cn } from "../lib/utils";
import { ClientOverviewTab } from "../components/client/ClientOverviewTab";
import { ClientVehiclesTab } from "../components/client/ClientVehiclesTab";
import { ClientUsersTab } from "../components/client/ClientUsersTab";
import { ClientComplianceTab } from "../components/client/ClientComplianceTab";
import { ClientAlertsTab } from "../components/client/ClientAlertsTab";
import { ClientDepartmentsTab } from "../components/client/ClientDepartmentsTab";
import { ClientSettingsTab } from "../components/client/ClientSettingsTab";
import { ClientInvoicesTab } from "../components/client/ClientInvoicesTab";

export const TABS: { key: string; label: string; icon: LucideIcon }[] = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "vehicles", label: "Vehicles", icon: Car },
  { key: "users", label: "Users", icon: Users },
  { key: "compliance", label: "Compliance", icon: ShieldCheck },
  { key: "alerts", label: "Alerts", icon: Bell },
  { key: "departments", label: "Departments", icon: Landmark },
  { key: "invoices", label: "Invoices", icon: Receipt },
  { key: "settings", label: "Settings", icon: Settings },
];

export default function ClientDetailPage() {
  const { id = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const tab = params.get("tab") || "overview";
  const action = params.get("action");
  const { data: t } = useQuery<Tenant>({ queryKey: ["tenant", id], queryFn: async () => (await api.get(`/tenants/${id}`)).data });
  const go = (next: string, extra?: Record<string, string>) => setParams({ tab: next, ...(extra || {}) });

  const [confirmToggle, setConfirmToggle] = useState(false);
  const [toggling, setToggling] = useState(false);
  const toggleActive = async () => {
    if (!t) return;
    setToggling(true);
    try {
      await api.put(`/tenants/${id}`, { is_active: !t.is_active });
      toast.success(t.is_active ? "Client deactivated" : "Client reactivated");
      qc.invalidateQueries({ queryKey: ["tenant", id] });
      qc.invalidateQueries({ queryKey: ["tenants"] });
      setConfirmToggle(false);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally { setToggling(false); }
  };

  if (!t) return <p className="text-sm text-slate-500">Loading client…</p>;
  const counts: Record<string, number | undefined> = { vehicles: t.vehicle_count, users: t.user_count, alerts: t.open_alerts };
  return (
    <div data-testid="client-detail-page">
      <Link to="/bo/clients" className="mb-3 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800" data-testid="back-to-clients-link"><ArrowLeft className="h-3 w-3" />All clients</Link>
      <header className="card-crisp mb-5 overflow-hidden rise">
        <div className="h-1.5" style={{ background: "var(--brand)" }} />
        <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-4">
          <div className="flex items-center gap-4">
            {t.branding.logo_file_id ? <img src={logoUrl(id, t.branding.logo_file_id)} alt={t.name} className="h-12 w-12 rounded-lg bg-slate-50 object-contain p-1 ring-1 ring-slate-200" data-testid="client-logo" /> : <span className="flex h-12 w-12 items-center justify-center rounded-lg text-sm font-bold text-white" style={{ background: "var(--brand)" }}>{(t.branding.display_name || t.name).slice(0, 2).toUpperCase()}</span>}
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Client · {t.slug}</p>
              <h1 className="font-display text-xl font-bold tracking-tight text-slate-900 sm:text-2xl" data-testid="client-name">{t.name}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500"><StatusBadge status={t.is_active ? "active" : "inactive"} testId="client-status-badge" />{t.registration_no && <span className="font-mono">{t.registration_no}</span>}{t.contact_email && <span>{t.contact_email}</span>}</div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => go("users", { action: "add" })} data-testid="client-add-user-button"><UserPlus className="h-3.5 w-3.5" />Add user</Button>
            <Button size="sm" onClick={() => go("vehicles", { action: "add" })} data-testid="client-register-vehicle-button"><Plus className="h-3.5 w-3.5" />Register vehicle</Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirmToggle(true)} data-testid="client-toggle-active-button" title={t.is_active ? "Deactivate client" : "Reactivate client"}><Power className={cn("h-3.5 w-3.5", t.is_active ? "text-rose-600" : "text-emerald-600")} /></Button>
            <ConfirmDialog open={confirmToggle} onCancel={() => setConfirmToggle(false)} onConfirm={toggleActive} busy={toggling} danger={t.is_active} testId="client-toggle-confirm"
              title={t.is_active ? `Deactivate ${t.name}?` : `Reactivate ${t.name}?`} confirmLabel={t.is_active ? "Deactivate client" : "Reactivate client"}
              description={t.is_active ? <><p>All <b>{t.name}</b> users will be signed out and can no longer log in. Vehicles, compliance records, invoices and history are kept and will not be billed while inactive.</p><p className="mt-2 text-xs text-slate-500">You can reactivate the client at any time from this page.</p></> : <p>Users of <b>{t.name}</b> will be able to sign in again and the client will be included in billing from the next cycle.</p>} />
          </div>
        </div>
        <nav className="flex overflow-x-auto border-t border-slate-100 px-2" data-testid="client-tabs">
          {TABS.map((tb) => (
            <button key={tb.key} type="button" data-testid={`client-tab-${tb.key}`} onClick={() => go(tb.key)}
              className={cn("relative flex shrink-0 items-center gap-1.5 px-3.5 py-3 text-sm font-semibold transition-colors", tab === tb.key ? "text-[color:var(--brand)]" : "text-slate-500 hover:text-slate-800")}>
              <tb.icon className="h-4 w-4" />{tb.label}
              {counts[tb.key] != null && <span className={cn("rounded-full px-1.5 text-[10px] font-bold", tab === tb.key ? "bg-indigo-50 text-[color:var(--brand)]" : "bg-slate-100 text-slate-600")}>{counts[tb.key]}</span>}
              {tab === tb.key && <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-[color:var(--brand)]" />}
            </button>
          ))}
        </nav>
      </header>
      {tab === "overview" && <ClientOverviewTab tenant={t} isNew={params.get("new") === "1"} onGo={go} />}
      {tab === "vehicles" && <ClientVehiclesTab tenant={t} autoOpen={action === "add"} onOpened={() => go("vehicles")} onVehicle={(vid) => navigate(`/bo/vehicles/${vid}`)} />}
      {tab === "users" && <ClientUsersTab tenant={t} autoOpen={action === "add"} onOpened={() => go("users")} />}
      {tab === "compliance" && <ClientComplianceTab tenant={t} />}
      {tab === "alerts" && <ClientAlertsTab tenant={t} />}
      {tab === "departments" && <ClientDepartmentsTab tenant={t} />}
      {tab === "invoices" && <ClientInvoicesTab tenant={t} />}
      {tab === "settings" && <ClientSettingsTab tenant={t} />}
    </div>
  );
}
